import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import Raven from 'raven-js';
import { Provider, connect } from 'react-redux';
import * as sessionActions from './features/session/redux/actions';
import { Switch, Route, BrowserRouter, useLocation } from 'react-router-dom';
import ErrorBoundary from './common/withBoundaries';
import Amplify, { Auth } from 'aws-amplify';
import {
  Authenticator,
  Greetings,
  SignIn,
  ConfirmSignIn,
  RequireNewPassword,
  SignUp,
  ConfirmSignUp,
  VerifyContact,
  TOTPSetup,
  Loading,
} from 'aws-amplify-react';
import Naluri from '~/common/api/naluri';
import { CognitoSignIn } from './features/cognito';
import { createClient } from './naluriApolloClient';
import { ApolloProvider } from '@apollo/react-hooks';
import { NaluriContext } from './common/naluri-context';
import { QueryClient, QueryClientProvider } from 'react-query';
import { currentImpersonatorToken } from './common/auth';
import ImpersonationOption from './pages/ImpersonationOption';
import firebaseCon from './common/analytics/firebase';
import { datadogRum } from '@datadog/browser-rum';
import LoadingComponent from '~/features/common/Loading';
import InfoBanner from '~/features/common/InfoBanner';

datadogRum.init({
  applicationId: "379478cc-68c3-4271-a4b8-5bb93928c9cf",
  clientToken: "pubc2913e6d8f7b034da85f90e395a188ae",
  site: "datadoghq.com",
  service: "coach-platform",
  env: process.env.NALURI_APP_ENV,
  version: '1.3.7',
  sampleRate: 90,
  sessionReplaySampleRate: 20,
  trackInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
})
    
datadogRum.startSessionReplayRecording();

Amplify.configure({
  Auth: {
    identityPoolId: process.env.NALURI_APP_COGNITO_IDENTITY_POOL_ID,
    region: process.env.NALURI_APP_COGNITO_REGION,
    userPoolId: process.env.NALURI_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.NALURI_APP_COGNITO_USER_POOL_WEB_CLIENT_ID,
    authenticationFlowType: 'USER_PASSWORD_AUTH',
  },
  Analytics: {
    disabled: true,
  },
});

const client = createClient(process.env.NALURI_APP_HEALTH_API);

// window.LOG_LEVEL = 'DEBUG';

const renderRouteConfigV3 = (routes, contextPath) => {
  const children = [];
  const renderRoute = (item, routeContextPath) => {
    let newContextPath = /^\//.test(item.path) ? item.path : `${routeContextPath}/${item.path}`;

    let Component = (Pages, props, children) => {
      return <Pages {...props}>{children}</Pages>;
    };

    newContextPath = newContextPath.replace(/\/+/g, '/');

    if (item.component && item.childRoutes) {
      const childRoutes = renderRouteConfigV3(item.childRoutes, newContextPath);
      children.push(
        <Route
          key={newContextPath}
          render={props => Component(item.component, props, childRoutes)}
          path={newContextPath}
        />,
      );
    } else if (item.component) {
      children.push(
        <Route key={newContextPath} path={newContextPath} exact component={item.component} />,
      );
    } else if (item.childRoutes) {
      item.childRoutes.forEach(r => renderRoute(r, newContextPath));
    }
  };

  routes.forEach(item => {
    if (item.path === '*') {
      children.push(
        <Route key="/impersonation-options" path="/impersonation-options">
          <ImpersonationOption />
        </Route>,
      );
    }
    renderRoute(item, contextPath);
  });

  return <Switch>{children}</Switch>;
};

const RouteTracker = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const screen = pathname.substring(pathname.lastIndexOf('/') + 1, pathname.length);

  React.useEffect(() => {
    if (screen){
      firebaseCon.analytics().logEvent('screen_view', { screen_name: screen });
    }
  }, [screen])
  
  return (null);
}

class Root extends React.Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    routeConfig: PropTypes.array.isRequired,
  };

  static contextType = NaluriContext;

  async componentDidMount() {
    window.__STORE__ = this.props.store;

    await Naluri.get('/api/me')
      .then(res => {
        if (!res.error && res.role !== 'member') {
          this.context.setAccessLevel(res.id);
          this.props.setSession({ id: res.id, name: res.name, role: res.role });
          datadogRum.setUser({
            id: res.id,
            role: res.role,
          });
        } else {
          Auth.signOut();
        }
      })
      .catch(error => {
        console.log({ error });
      });
  }

  componentDidCatch(error, errorInfo) {
    Raven.captureException(new Error(error), {
      extra: { info: errorInfo },
    });
  }

  render() {
    const children = renderRouteConfigV3(this.props.routeConfig, '/');
    const queryClient = new QueryClient();
    return (
      <ErrorBoundary>
        <Provider store={this.props.store}>
          <ApolloProvider client={client}>
            <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingComponent />}>
                <BrowserRouter>
                  <InfoBanner />
                  <RouteTracker/>
                    {children}
                </BrowserRouter>
              </Suspense>
              </ErrorBoundary>
            </QueryClientProvider>
          </ApolloProvider>
        </Provider>
      </ErrorBoundary>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setSession: data => dispatch(sessionActions.setSession(data)),
  };
};

const RootWithDispatch = connect(null, mapDispatchToProps)(Root);

class RootWrapper extends React.Component {
  state = {
    authState: null,
    authData: null,
    impersonateLogin: false,
  };

  componentDidMount() {
    if (currentImpersonatorToken()) {
      this.setState({ impersonateLogin: true });
    }
  }

  handleAuthStateChange = (state, data) => {
    if (state === 'signedIn') {
      // Attempt to redirect when state change to signedIn.

      // If we don't place it here, the setState below, will
      // then lead to a separate redirection where I have no
      // idea how to control off.
      this.redirectBackIfExist();
    }

    if (state === 'impersonateLogin') {
      this.setState({ impersonateLogin: true });
    }

    this.setState({ authState: state, authData: data });
  };

  redirectBackIfExist = () => {
    const currentURL = new URL(window.location.href);
    const redirectBackURL = currentURL.searchParams.get('redirect_back');

    if (redirectBackURL && redirectBackURL !== '') {
      window.location.href = redirectBackURL;
    }
  };

  get signedIn() {
    const signedIn = this.state.impersonateLogin || this.state.authState === 'signedIn';
    return signedIn;
  }

  transformCognitoErrorMessage(message) {
    if (/incorrect.*username.*password/i.test(message)) {
      return 'Incorrect email or password';
    }

    if (/validation.*error.*/i.test(message)) {
      return 'Invalid username';
    }

    return message;
  }

  render() {
    return (
      <React.Fragment>
        {this.signedIn ? (
          <RootWithDispatch authData={this.state.authData} {...this.props} />
        ) : (
          <Authenticator
            errorMessage={this.transformCognitoErrorMessage}
            hide={[
              Greetings,
              SignIn,
              ConfirmSignIn,
              RequireNewPassword,
              SignUp,
              ConfirmSignUp,
              VerifyContact,
              TOTPSetup,
              Loading,
            ]}
            onStateChange={this.handleAuthStateChange}
            onAuthUIStateChange={this.onAuthUIStateChange}
          >
            <CognitoSignIn override={'SignIn'} />
          </Authenticator>
        )}
      </React.Fragment>
    );
  }
}

export default RootWrapper;



// WEBPACK FOOTER //
// ./src/Root.js